import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { getFeaturedComics, getLatestComics, getComicsByCategory, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function ComicCard({ comic, featured }: { comic: Comic; featured?: boolean }) {
  return (
    <Link
      to={`/article/${comic.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[5/7] mb-3">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 韩国漫画`}
          title={`${comic.title} - 桃桃漫画网在线免费阅读`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
            featured
              ? 'bg-accent-500 text-foreground-950'
              : 'bg-background-50/90 text-foreground-700'
          }`}>
            {comic.status}
          </span>
        </div>
        {comic.rating >= 4.8 && (
          <div className="absolute top-3 right-3 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-full font-medium">
            高分
          </div>
        )}
      </div>
      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
        {comic.title}
      </h3>
      <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
        <span className="flex items-center gap-1">
          <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
          {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-star-fill w-3.5 h-3.5 flex items-center justify-center text-accent-500"></i>
          {comic.rating}
        </span>
      </div>
    </Link>
  );
}

export default function Home() {
  const featuredComics = getFeaturedComics();
  const latestComics = getLatestComics(6);

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '桃桃漫画网',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '桃桃漫画网是韩国漫画画免费画漫画大全，海量韩国漫画资源，支持在线免费阅读，涵盖浪漫爱情、奇幻冒险、都市生活等多种分类。',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${typeof window !== 'undefined' ? window.location.origin : ''}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '桃桃漫画网',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '韩国漫画在线阅读平台，海量漫画免费阅读',
    sameAs: ['http://lcwb.cn/', 'http://lcwb.cn/taotao'],
  };

  return (
    <>
      <SEOHead
        title="韩国漫画_韩国漫画画免费画漫画大全_桃桃漫画网"
        description="桃桃漫画网是韩国漫画画免费画漫画大全，海量韩国漫画资源，支持在线免费阅读，涵盖浪漫爱情、奇幻冒险、都市生活、悬疑推理、动作热血等多种分类，让您尽情享受绘画与阅读的乐趣。"
        keywords="韩国漫画,画免费,画漫画大全,桃桃漫画网,在线阅读,韩国漫画免费"
        schemaData={[homepageSchema, orgSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative h-[420px] md:h-[600px] w-full overflow-hidden">
            <img
              src="http://lcwb.cn/ai/img_res/edited_e1aca6bbfebb00f758d67294fc5e76fc_3456e8d8.jpg"
              alt="桃桃漫画网 - 韩国漫画在线阅读平台"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
            <div className="relative z-10 h-full flex items-center">
              <div className="w-full px-4 md:px-6 lg:px-12">
                <div className="max-w-2xl">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-4">
                    发现你的<br />漫画世界
                  </h1>
                  <p className="text-sm md:text-base text-background-50/80 max-w-lg leading-relaxed mb-8">
                    海量韩国漫画资源，随时随地免费阅读。浪漫爱情、奇幻冒险、悬疑推理——你的下一部最爱，就在这里。
                  </p>
                  <Link
                    to="/category/romance"
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-7 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    开始探索
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Featured Comics Section */}
          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
                <div>
                  <span className="text-xs font-medium text-foreground-500 uppercase tracking-wider">
                    TAOTAO COMICS
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-1 font-heading">
                    热门推荐
                  </h2>
                </div>
                <p className="text-sm text-foreground-600 max-w-md leading-relaxed">
                  精选口碑佳作，每一部都值得细细品味。从浪漫爱情到热血动作，发现属于你的精彩故事。
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                {featuredComics.map((comic, index) => (
                  <div
                    key={comic.id}
                    className={index === 0 ? 'col-span-2 row-span-2' : ''}
                    data-product-shop=""
                  >
                    {index === 0 ? (
                      <Link to={`/article/${comic.id}`} className="group block bg-foreground-900 rounded-lg overflow-hidden h-full cursor-pointer">
                        <div className="relative h-full flex flex-col">
                          <img
                            src={comic.coverImage}
                            alt={`${comic.title} - 韩国漫画免费阅读`}
                            className="absolute inset-0 w-full h-full object-cover object-top opacity-75 group-hover:scale-105 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-transparent"></div>
                          <div className="relative z-10 mt-auto p-5 md:p-6">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="bg-primary-500/20 text-primary-300 text-xs px-2.5 py-1 rounded-full">
                                {comic.category}
                              </span>
                              <span className="text-background-50/60 text-xs">{comic.chapters}话</span>
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-2">{comic.title}</h3>
                            <p className="text-background-50/60 text-xs line-clamp-2 leading-relaxed">{comic.description}</p>
                          </div>
                        </div>
                      </Link>
                    ) : index === 1 ? (
                      <Link to={`/article/${comic.id}`} className="group block rounded-lg overflow-hidden h-full relative cursor-pointer">
                        <img
                          src={comic.coverImage}
                          alt={`${comic.title} - 韩国漫画在线免费`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="inline-flex items-center gap-1 bg-foreground-900/60 text-background-50 text-xs px-2.5 py-1 rounded-full">
                            <i className="ri-bookmark-line w-3 h-3 flex items-center justify-center"></i>
                            {comic.category}
                          </span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h3 className="text-lg font-bold text-background-50">{comic.title}</h3>
                        </div>
                      </Link>
                    ) : (
                      <ComicCard comic={comic} featured />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
                <div>
                  <h2 className="text-lg font-bold text-foreground-400 uppercase tracking-wider">
                    最新更新
                  </h2>
                  <p className="text-2xl md:text-3xl font-bold text-foreground-900 mt-1 font-heading italic">
                    新鲜出炉的精彩内容
                  </p>
                </div>
                <Link
                  to="/category/romance"
                  className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
                {latestComics.map((comic, index) => (
                  <div key={comic.id} data-product-shop="">
                    <Link
                      to={`/article/${comic.id}`}
                      className={`group block rounded-lg overflow-hidden cursor-pointer ${
                        index === 1 ? 'bg-foreground-900' : 'bg-background-50'
                      }`}
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={comic.coverImage}
                          alt={`${comic.title} - 在线免费韩国漫画`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs mb-2 ${
                          index === 1
                            ? 'bg-accent-500/20 text-accent-300'
                            : 'bg-accent-100 text-accent-800'
                        }`}>
                          {comic.category}
                        </span>
                        <h3 className={`text-sm font-semibold line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors duration-200 ${
                          index === 1 ? 'text-background-50' : 'text-foreground-900'
                        }`}>
                          {comic.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                          <span>{comic.author}</span>
                          <span>更新至{comic.chapters}话</span>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Categories Section */}
          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  探索分类
                </h2>
                <p className="text-sm text-foreground-600 mt-2">找到你喜欢的漫画类型，开启阅读之旅</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex items-center justify-between bg-background-100 hover:bg-primary-50 rounded-lg px-4 py-4 md:px-5 md:py-5 transition-all duration-200 cursor-pointer"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-400 bg-background-200/70 group-hover:bg-primary-100 px-2 py-0.5 rounded-full transition-colors duration-200">
                      {cat.count}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-[800px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-600 mt-2">关于桃桃漫画网的常见疑问解答</p>
              </div>
              <div className="space-y-3">
                {[
                  { q: '桃桃漫画网的漫画是否免费？', a: '是的，桃桃漫画网上的所有韩国漫画资源均完全免费，您无需支付任何费用即可畅享海量漫画内容。我们致力于为漫画爱好者提供免费的阅读体验。' },
                  { q: '支持哪些设备阅读？', a: '桃桃漫画网全面支持电脑、平板和手机端阅读，自适应所有屏幕尺寸。无论您使用什么设备，都能获得流畅舒适的阅读体验。' },
                  { q: '漫画更新频率如何？', a: '我们与多家韩国漫画平台保持合作，热门漫画通常每日更新，确保您在第一时间阅读到最新章节。具体更新频率视作品而定。' },
                  { q: '如何找到想看的漫画？', a: '您可以通过首页的分类导航浏览不同类型，也可以使用搜索功能查找特定作品。热门推荐和最新更新板块也会帮助您发现精彩内容。' },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-50 rounded-lg">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer">
                      <span className="text-sm md:text-base font-medium text-foreground-800">{faq.q}</span>
                      <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                    </summary>
                    <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}